import { VIDEOS } from "@/data/videos";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, collectionPageSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { videoObjectNode } from "@/lib/media-schema";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Calendar, Clock, BookOpen, ArrowRight, Tag } from "lucide-react";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/blog/")({
    loader: async () => {
    const { BLOG_INDEX } = await import("@/data/blog-index.th.gen");
    return { __d: { BLOG_INDEX } };
  },
head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle("Knowledge Hub · บทความและคู่มือ — NYC Language Institute", "/blog", "th")},
      {
        name: "description",
        content:
          ctaDesc("บทความครบเครื่องเรื่อง Notary, Apostille, NAATI, Legal Translation, LTR Visa — ปรึกษาฟรี ประเมินงานให้ทันที ทักไลน์ได้เลย", "/blog", "th"),
      },
      { property: "og:title", content: ctaTitle("Knowledge Hub — NYC Language Institute", "/blog", "th")},
      { property: "og:description", content: ctaDesc("คู่มือฉบับสมบูรณ์เรื่องเอกสาร กฎหมาย และวีซ่าในไทย", "/blog", "th")},
      { property: "og:url", content: `${SITE}/blog` },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: `${SITE}/blog` }, ...hreflangLinks("/blog")],
    scripts: [speakableSchema(), consolidateGraph([
      collectionPageSchema({ path: "/blog", name: "NYC Legal & Notary Knowledge Hub", description: "บทความครบเครื่องเรื่อง Notary, Apostille, NAATI, Legal Translation, LTR Visa, Probate และ BOI", itemCount: loaderData!.__d.BLOG_INDEX.length }),
      {
        type: "application/ld+json" as const,
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "NYC Legal & Notary Knowledge Hub",
          url: `${SITE}/blog`,
          blogPost: loaderData!.__d.BLOG_INDEX.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `${SITE}/blog/${p.slug}`,
            datePublished: p.publishedAt,
            dateModified: p.updatedAt,
          })),
        }),
      },
      {
        type: "application/ld+json" as const,
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
          ],
        }),
      },
      // P21 — VideoObject (with transcript) for videos tied to /blog
      ...VIDEOS.filter((v) => v.pagePath === "/blog").map((v) => ({
        type: "application/ld+json" as const,
        children: JSON.stringify(videoObjectNode({
          name: v.title,
          description: v.description,
          thumbnailUrl: v.thumbnailUrl,
          uploadDate: v.publicationDate,
          duration: v.duration,
          contentUrl: v.contentUrl,
          embedUrl: v.playerUrl,
          pageUrl: `${SITE}${v.pagePath}`,
          transcript: v.transcript,
        })),
      })),
    ])],
  }),
  component: BlogIndex,
});

import { blogCover } from "@/data/blog-covers";

const CATEGORY_COLOR: Record<string, string> = {
  Notary: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Translation: "bg-sky-50 text-sky-700 border-sky-200",
  Visa: "bg-violet-50 text-violet-700 border-violet-200",
  Legalization: "bg-amber-50 text-amber-700 border-amber-200",
  Legal: "bg-rose-50 text-rose-700 border-rose-200",
};

function BlogIndex() {
  const { BLOG_INDEX } = (Route.useLoaderData() as { __d: typeof import("@/data/blog-index.th.gen") }).__d;
  const [featured, ...rest] = BLOG_INDEX;

  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-slate-50">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-navy-deep text-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <BookOpen className="h-3.5 w-3.5" /> Knowledge Hub
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-5xl">
            คู่มือและบทความ <span className="text-gold">โดยทีมทนายและนักแปล NYC</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/80 sm:text-lg">
            บทความเชิงลึกเรื่อง Notary Public, Apostille, NAATI, Legal Translation, LTR Visa, Probate และ BOI — อัปเดตทุกเดือนโดยผู้เชี่ยวชาญที่ทำเคสจริง
          </p>
          <div className="quick-answer mt-6 max-w-3xl rounded-2xl border border-gold/40 bg-gold/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-gold">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">บทความและคู่มือ Notary, Apostille และ Visa ที่อัปเดตล่าสุดอยู่ที่ไหน?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">NYC Language Institute เผยแพร่บทความเชิงลึกอัปเดตทุกเดือนจากทีมทนายและนักแปลตัวจริง ครอบคลุม Notary Public, Hague Apostille (ไทยเข้าร่วม เมื่อ 30 มิ.ย. 2569 (มีผลบังคับใช้กับไทย 28 ก.พ. 2570)), NAATI, LTR Visa, Non-B, DTV, BOI, Probate และ Legal Translation — พร้อมตัวอย่างเคสจริง ราคา และเช็คลิสต์เอกสาร</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
        {/* Featured */}
        <Link
          to="/blog/$slug"
          params={{ slug: featured.slug }}
          className="group block overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-xl"
        >
          <div className="grid gap-0 md:grid-cols-5">
            <div className="relative flex items-center justify-center overflow-hidden bg-gradient-to-br from-navy-deep via-navy-deep to-[#1a2548] p-8 text-white md:col-span-2">
              <img
                src={blogCover(featured.category, "th").src}
                alt={blogCover(featured.category, "th").altTh}
                width={1200}
                height={630}
                fetchPriority="high"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover opacity-35"
              />
              <div className="relative text-center">
                <div className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold ${CATEGORY_COLOR[featured.category]} bg-white/95`}>
                  <Tag className="h-3 w-3" /> {featured.category}
                </div>
                <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.25em] text-gold">Featured</div>
                <div className="mt-2 font-display text-4xl font-extrabold leading-tight text-gold md:text-5xl">{featured.readMinutes} min</div>
                <div className="mt-1 text-[11px] text-white/70">เวลาอ่านโดยประมาณ</div>
              </div>
            </div>
            <div className="p-7 md:col-span-3 md:p-10">
              <h2 className="font-display text-2xl font-extrabold leading-snug text-navy-deep transition group-hover:text-amber-600 md:text-3xl">
                {featured.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">{featured.excerpt}</p>
              <div className="mt-5 flex flex-wrap items-center gap-4 text-[12px] text-slate-500">
                <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> อัปเดต {featured.updatedAt}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {featured.readMinutes} นาที</span>
                <span>By {featured.author}</span>
              </div>
              <div className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-amber-600 group-hover:gap-2 transition-all">
                อ่านบทความเต็ม <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
        </Link>

        {/* Grid */}
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((p) => (
            <Link
              key={p.slug}
              to="/blog/$slug"
              params={{ slug: p.slug }}
              className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:border-gold/40 hover:shadow-lg"
            >
              <img
                src={blogCover(p.category, "th").src}
                alt={blogCover(p.category, "th").altTh}
                width={1200}
                height={630}
                loading="lazy"
                decoding="async"
                className="aspect-[1200/630] w-full object-cover"
              />
              <div className="flex flex-1 flex-col p-6">
              <div className={`inline-flex w-fit items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${CATEGORY_COLOR[p.category]}`}>
                <Tag className="h-3 w-3" /> {p.category}
              </div>
              <h3 className="mt-3 flex-1 font-display text-lg font-extrabold leading-snug text-navy-deep transition group-hover:text-amber-600">
                {p.title}
              </h3>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-600 line-clamp-3">{p.excerpt}</p>
              <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-[11px] text-slate-500">
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.readMinutes} นาที</span>
                <span>{p.updatedAt}</span>
              </div>
              </div>
            </Link>
          ))}
        </div>
      <RelatedHubGrid locale="th" />
      </div>
    </main>
  );
}
